import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import useFormSchema from "../formModels/useFormSchema";
import SimpleTextField from "./SimpleTextField";
import NumericTextField from "./NumericTextField";
import YesNoFieldWithAlert from "./YesNoFieldWithAlert";
import LanguageValueField from "./LanguageValueField";
import AdmsIdentifierList from "../formModels/AdmsIdentifierList";
import AdmsIdentifier from "../formModels/AdmsIdentifier";
import LanguageValueList from "../formModels/LanguageValueList";
import GenericLangValueList from "../formModels/GenericLangValueList";
import Address from "../formModels/Address";
import Person from "../formModels/Person";
import PersonSingle from "./PersonSingle";
import AddressView from "./AddressView";
import TextFieldList from "./TextFieldList";
import AdmsIdentifierListView from "./AdmsIdentifierListView";
import AutocompleteChipsView from "./AutocompleteChipsView";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as RegisterIcon } from "../assets/register.svg";
import { getTitleByField, getDescriptionByField, parseValidationErrors, cleanEmptyFields, replaceIdOnlyObjectsWithNull, validateFormData } from "../config/commonFunctions"; // validateFormData
import {
  Stack, Button, Link, Typography, Paper, Checkbox, FormControlLabel,
  CircularProgress, Switch, Container, Alert, Box, FormControl, FormLabel,
  Modal, IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ErrorIcon from '@mui/icons-material/Error';
import AutocompleteSingle from "./AutocompleteSingle";
import { PARTICIPANT_FORM_SUBMITION, PARTICIPANT_DISPLAY, PARTICIPANT_FORM_SCHEMA, BASE_URL } from "../config/settings";
import { USER_ACTIONS, isAllowedAction } from "../config/auth";
import { messages } from "../config/messages";

const initialFormData = {
  "address": new Address(),
  "other_address": new Address(),
  "legal_representative": new Person(),
  "technical_contact": new Person(),
  "identifier": new AdmsIdentifierList(),
  "name": new LanguageValueList(),
  "short_name": new LanguageValueList(),
  "activities": new GenericLangValueList(),
  "description": new LanguageValueList(),
  "vat": "",
  "organization_type": "",
  "company_type": "",
  "location_of_incorporation": "",
  "contact_website": [],
  "eu_id": "",
  "pic_number":"",
  "oca_project": "",
  "eu_controlled_entity":"",
};
const ENDPOINT_URL = PARTICIPANT_FORM_SUBMITION;

const steps = [messages.step1Title, messages.step2Title];

const ParticipantForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // const editingParticipant = location.state?.participant || null;
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false); // To track edit mode
  const [participantId, setParticipantId] = useState(null); // For the PUT request
  const [globalLanguage, setGlobalLanguage] = useState("en"); // State for global language
  const [schemaAsObject, setSchemaAsObject] = useState({});
  const [dataToSave, setDataToSave] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [companyTypeOptions, setCompanyTypeOptions] = useState([]);
  const [country, setCountry] = useState([]);
  const [hasAcknowledgedTerms, setHasAcknowledgedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [optOut, setOptOut] = useState(false);
  // const [activeStep, setActiveStep] = useState(0); // 0 = form, 1 = documents
  // const [completedSteps, setCompletedSteps] = useState({0: false});
  const [participantCreated, setParticipantCreated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispableOptIn = (isEditMode && !isAllowedAction(USER_ACTIONS.UPDATE_OWN_FORM)) ? true: false;
  const isFieldRequired = (field) => {
    // Check if required array exists in the schema
    return schemaAsObject?.required?.includes(field) ?? false;
  };

  const {
    formData,
    handleChange,
    handleIdentifierChange,
    handleAddressChange,
    handlePersonChange,
    handleFieldChange,
    // handleLogoUpload,
  } = useFormSchema(initialFormData);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  /* useEffect(() => {
    serializeFormData();
  }, [formData]); */

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true); // Set loading state
        const url = PARTICIPANT_FORM_SCHEMA + globalLanguage + "/";
        // console.log("Fetching URL:", url);
        const result = await axios.get(url);
        setSchemaAsObject(result.data);
        // console.log("Fetched Schema:", result.data);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching schema:", err);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [globalLanguage]);

  useEffect(() => {
    if (schemaAsObject?.$defs?.CompanyType) {
      setCompanyTypeOptions(schemaAsObject.$defs.CompanyType.vocab); // Ensure $defs and CompanyType exist
    }
    if (schemaAsObject?.$defs?.Country) {
      setCountry(schemaAsObject.$defs.Country.vocab); // Ensure $defs and CompanyType exist
    }
  }, [schemaAsObject]);
  // console.log(schemaAsObject);

  useEffect(() => {
    if (id) {
        setIsEditMode(true);
        setParticipantId(id);
    }
}, [id]);

  useEffect(() => {
    if (isEditMode && participantId) {
      fetchParticipant();
    }
  }, [isEditMode, participantId]);

const fetchParticipant = async () => {
  try {
      const res = await axios.get(PARTICIPANT_DISPLAY(participantId));
      // console.log(res.data);
      setEditingParticipant(res.data);
  } catch (err) {
      console.error("Error fetching documents:", err);
  }
};

  // Check if we're in edit mode by looking for participant data in location.state
  useEffect(() => {
    if (editingParticipant) {
      setHasAcknowledgedTerms(true);
      setOptOut(!!editingParticipant?.management.opt_out_central_catalogue); //  cast a value to a boolean
      // console.log("editingParticipant", editingParticipant); //location.state.participant
      // Prefill formData with the participant's data
      const prepopulateFormData = {
        name: new LanguageValueList(editingParticipant.name),
        short_name: new LanguageValueList(editingParticipant.short_name),
        activities: new GenericLangValueList(editingParticipant.activities),
        description: new LanguageValueList(editingParticipant.description),
        vat: editingParticipant.vat,
        organization_type: editingParticipant.organization_type,
        company_type: editingParticipant.company_type,
        location_of_incorporation: editingParticipant.location_of_incorporation,
        contact_website: editingParticipant.contact_website,
        eu_id: editingParticipant.eu_id,
        address: new Address(new LanguageValueList(editingParticipant.address.street_address),
          new LanguageValueList(editingParticipant.address.administrative_area),
          new LanguageValueList(editingParticipant.address.city),
          editingParticipant.address.postal_code,
          editingParticipant.address.country,
          editingParticipant.address.id),
        other_address: editingParticipant.other_address ? new Address(new LanguageValueList(editingParticipant.other_address.street_address),
          new LanguageValueList(editingParticipant.other_address.administrative_area),
          new LanguageValueList(editingParticipant.other_address.city),
          editingParticipant.other_address.postal_code,
          editingParticipant.other_address.country,
          editingParticipant.other_address.id) : new Address(),
        identifier: (Array.isArray(editingParticipant.identifier) && editingParticipant.identifier.length > 0)
          ? new AdmsIdentifierList(
            editingParticipant.identifier.map(
              item => new AdmsIdentifier(item.identifier_notation, new LanguageValueList(item.creator), item.id)
            )
          )
          : new AdmsIdentifierList(),
        legal_representative: new Person(new LanguageValueList(editingParticipant.legal_representative.name), editingParticipant.legal_representative.email, editingParticipant.legal_representative.id),
        technical_contact: editingParticipant.technical_contact ? new Person(new LanguageValueList(editingParticipant.technical_contact.name), editingParticipant.technical_contact.email, editingParticipant.technical_contact.id) : new Person(),
        pic_number: editingParticipant.pic_number,
        oca_project: editingParticipant.oca_project,
        eu_controlled_entity: editingParticipant.eu_controlled_entity,
        // logo: editingParticipant.logo,
        // connector: editingParticipant.connector,
      };
      // console.log("prepopulateFormData", prepopulateFormData);
      // Update the formData in useFormSchema hook with the pre-populated data
      handleFieldChange("name", prepopulateFormData.name);
      handleFieldChange("short_name", prepopulateFormData.short_name);
      handleFieldChange("activities", prepopulateFormData.activities);
      handleFieldChange("description", prepopulateFormData.description);
      handleChange("vat", prepopulateFormData.vat);
      handleChange("organization_type", prepopulateFormData.organization_type);
      handleChange("company_type", prepopulateFormData.company_type);
      handleChange("location_of_incorporation", prepopulateFormData.location_of_incorporation);
      handleChange("contact_website", prepopulateFormData.contact_website);
      handleChange("eu_id", prepopulateFormData.eu_id);
      handleIdentifierChange(prepopulateFormData.identifier.getAllModels());
      handlePersonChange("legal_representative", prepopulateFormData.legal_representative);
      handleAddressChange("address", prepopulateFormData.address);
      handleAddressChange("other_address", prepopulateFormData.other_address);
      handlePersonChange("technical_contact", prepopulateFormData.technical_contact);
      handleChange("pic_number", prepopulateFormData.pic_number);
      handleChange("oca_project", prepopulateFormData.oca_project);
      handleChange("eu_controlled_entity", prepopulateFormData.eu_controlled_entity);
      // handleChange("logo", prepopulateFormData.logo);
      // handleChange("connector", prepopulateFormData.connector);
    }
  }, [editingParticipant]);

  const handleOptOutChange = async (event) => {
    // `event.target.checked` is TRUE when the switch is ON (Opt-In), FALSE when OFF (Opt-Out).
    // We want `newOptOutStatus` to be TRUE if the user is opting OUT, FALSE if Opting IN.
    const newOptOutStatus = !event.target.checked;
    setOptOut(newOptOutStatus); // update local optOut state
  };

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
  };
  // Function to serialize formData into a simple JSON object
  const serializeFormData = (currentFormData) => {
    const serializedData = {
      name: currentFormData.name.toJSON(), // Converts to array of language-value pairs
      short_name: currentFormData.short_name.toJSON(),
      activities: currentFormData.activities.toJSON(),
      description: currentFormData.description.toJSON(),
      vat: currentFormData.vat,
      organization_type: currentFormData.organization_type,
      company_type: currentFormData.company_type,
      location_of_incorporation: currentFormData.location_of_incorporation,
      contact_website: currentFormData.contact_website,
      eu_id: currentFormData.eu_id,
      pic_number: currentFormData.pic_number,
      oca_project: currentFormData.oca_project,
      eu_controlled_entity: currentFormData.eu_controlled_entity,
      address: currentFormData.address.toJSON(),
      other_address: currentFormData.other_address.toJSON(),
      identifier: currentFormData.identifier.toJSON(),
      legal_representative: currentFormData.legal_representative.toJSON(),
      technical_contact: currentFormData.technical_contact.toJSON(),
    };
    return serializedData;
    // setDataToSave(serializedData); // Update the dataToSave state
  };

  const handleAcknowledgeChange = (event) => {
    setHasAcknowledgedTerms(event.target.checked);
  };

  /* useEffect(() => {
    if (formData.eu_controlled_entity === false) {
      handleChange("pic_number", "");
      handleChange("oca_project", "");
    }
  }, [formData.eu_controlled_entity]); */

  const handleSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setLoading(true);
    const finalToSave = serializeFormData(formData);
    if (isEditMode) {
      // PUT request for editing existing participant
      const cleanedDataStep1 = cleanEmptyFields(finalToSave);
      const cleanedDataStep2 = replaceIdOnlyObjectsWithNull(cleanedDataStep1);
      const cleanedData = {
        ...cleanedDataStep2,
        management: {
          opt_out_central_catalogue: optOut ? true : false,
        },
      };
      // console.log("Data to save:", cleanedData);
      axios
        .put(`${ENDPOINT_URL}${participantId}/`, cleanedData)
        .then(async (response) => {
            navigate("/management");
        })
        .catch((err) => {
          if (err.response && err.response.status === 400) {
            setValidationError(err.response.data);
          } else {
            console.error("Unexpected error occurred:", err);
          }
        })
        .finally(() => {
          setLoading(false);
          setIsSubmitting(false);
        });
    } else {
      // POST request for creating a new participant
      const cleanedDataStep1 = cleanEmptyFields(finalToSave);
      const cleanedData = {
        ...cleanedDataStep1,
        management: {
          opt_out_central_catalogue: optOut ? true : false,
        },
      };
      console.log("Data to save:", cleanedData);
      axios
        .post(ENDPOINT_URL, cleanedData)
        .then(async (response) => {
          const id = response.data.id;
          setParticipantId(id);
          setParticipantCreated(true);
          navigate("/submission-success");
        })
        .catch((err) => {
          if (err.response && err.response.status === 400) {
            setValidationError(err.response.data);
          } else {
            console.error("Unexpected error occurred:", err);
          }
        })
        .finally(() => {
          setLoading(false);
          setIsSubmitting(false);
        });
    }
  };

  function printError(errorMessages) {
    if (!errorMessages) {
      return <></>;
    }
    // console.log(errorMessages);
    const errors = parseValidationErrors(errorMessages);
    // Split the errors string into an array by newline characters
    const errorLines = errors.split("\n").filter((line) => line.trim() !== "");
    return (
      <Paper style={{ padding: "1%" }}>
        <div className="section--title--warning flex flex--center">
          <span><ErrorIcon className="nav-icon-xlarge" /></span>
          <span><Typography variant="h6" noWrap>{messages.paticipantFormValidationErrors}</Typography></span>
        </div>
        <div>
          {errorLines.map((error, index) => (
            <div key={index} style={{ paddingBottom: "0.5em" }}>{error}</div> // Each error rendered in its own div
          ))}
        </div>
      </Paper>
    );
  }

  return (<Container maxWidth="xl" sx={{ mb: 2 }}>
    <Grid container direction="row" spacing={2} sx={{ alignItems: "center", pl: 2, pr: 2 }}>
      <Grid size={2}>
        <RegisterIcon className="page-illustration" />
      </Grid>
      <Grid size={8}>
        <Typography variant="h2" sx={{ mb: 1, mt: 5 }}>
          {!isEditMode ? messages.participantFormTitle : messages.participantFormOrgGroup}
        </Typography>
        <Typography variant="h5" sx={{ mb: 3, mt: 1 }}>
          {messages.participantFormSubTitle}
        </Typography>
      </Grid>
    </Grid>

    {/* <Stepper nonLinear activeStep={activeStep} style={{marginBottom: "2em"}}>
      {steps.map((label, index) => (
        <Step key={label} completed={completedSteps[index]}>
          <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton >
        </Step>
      ))}
      </Stepper> */}
    {true && (
      <Stack spacing={4}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            {/* Opt-in/Opt-out Toggle Switch */}
            {true && (
              <Paper sx={{
                backgroundColor:"#D1E2F8", // or use `theme.palette.action.hover`
                borderRadius: 2,
                px: 3,
                py: 2,
                mb: 3,
              }}>
                <Typography variant="h5" gutterBottom>{messages.catalogueAsk} {" "}
                  <Link href={`${BASE_URL}/broker/list-of-participants`} target="_blank" rel="noopener noreferrer"
                    style={{ color: "#0F2651", fontWeight: "600" }}>
                    {messages.catalogueAskLinkText}
                  </Link>
                  <span>{" "}?</span>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!optOut} // checked means opt-in, so it's !optOut
                      onChange={handleOptOutChange}
                      name="optOutToggle"
                      color="primary"
                      disabled={dispableOptIn}
                    />
                  }
                  label={
                    optOut ? (
                      <Typography color="textSecondary">{messages.participantFormOptOutMessage}</Typography>
                    ) : (
                      <Typography color="primary">{messages.participantFormOptInMessage}</Typography>
                    )
                  }
                />
                {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
                <Typography variant="body1" sx={{ fontStyle: "italic", pt: 1 }}>{messages.catalogueAskNote}</Typography>
              </Paper>
            )}

            <LanguageValueField
              id={"name-id"}
              label={getTitleByField(schemaAsObject, "name")}
              helperText={getDescriptionByField(schemaAsObject, "name")}
              obj={formData?.name}
              lang={globalLanguage}
              required={isFieldRequired("name")}
              onChange={(updatedValues) => handleFieldChange("name", updatedValues)}
            // error={formErrors.allErrors.name}
            />

            <SimpleTextField
              label={getTitleByField(schemaAsObject, "vat")}
              helperText={getDescriptionByField(schemaAsObject, "vat") + ". If you do not have a VAT number  please enter -"}
              value={formData.vat || ""}
              onChange={(value) => handleChange("vat", value)}
              required={isFieldRequired("vat")}
            // error={formErrors.allErrors.vat}
            />

            <SimpleTextField
              label={getTitleByField(schemaAsObject, "organization_type")}
              helperText={getDescriptionByField(schemaAsObject, "organization_type")}
              value={formData.organization_type || ""}
              onChange={(value) => handleChange("organization_type", value)}
              required={isFieldRequired("organization_type")}
            // error={formErrors.allErrors.organization_type}
            />

            <AutocompleteSingle
              title={getTitleByField(schemaAsObject, "company_type")}
              helperText={getDescriptionByField(schemaAsObject, "company_type")}
              options={companyTypeOptions}
              onChange={(value) => handleChange("company_type", value)} // Update with the value selected
              required={isFieldRequired("company_type")}
              value={
                formData.company_type
                  ? companyTypeOptions.find(option => option.value === formData.company_type)
                  : null // If no prepopulated data, set to null to display empty field
              }
            // error={formErrors.allErrors.company_type}
            />

            <AutocompleteSingle
              title={getTitleByField(schemaAsObject, "location_of_incorporation")}
              helperText={getDescriptionByField(schemaAsObject, "location_of_incorporation")}
              options={country}
              onChange={(value) => handleChange("location_of_incorporation", value)} // Update with the value selected
              required={isFieldRequired("location_of_incorporation")}
              value={
                formData.location_of_incorporation
                  ? country.find(option => option.value === formData.location_of_incorporation)
                  : null // If no prepopulated data, set to null to display empty field
              }
            // error={formErrors.allErrors.location_of_incorporation}
            />

            <TextFieldList
              id={"contact_website"}
              title={getTitleByField(schemaAsObject, "contact_website")}
              subtitle={getDescriptionByField(schemaAsObject, "contact_website")}
              values={formData?.contact_website} // Pass the websites state as values
              onChange={(values) => handleChange("contact_website", values)}
              required={isFieldRequired("contact_website")}
            // error={formErrors.allErrors.contact_website}
            />

            <AddressView address={formData.address}
              handleChange={(updatedAddress) => handleAddressChange("address", updatedAddress)}
              fieldName="address"
              title={getTitleByField(schemaAsObject, "address")}
              lang={globalLanguage}
              countryOptions={country}
              schemaAsObject={schemaAsObject}
              subtitle={getDescriptionByField(schemaAsObject, "address")}
              required={isFieldRequired("address")}
            // error={formErrors.addressErrors}
            />

          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <PersonSingle
            title={getTitleByField(schemaAsObject, "legal_representative")}
            subtitle={getDescriptionByField(schemaAsObject, "legal_representative")}
            id={"legal_representative"}
            initialPerson={formData.legal_representative}
            onChange={(updatedPerson) => handlePersonChange("legal_representative", updatedPerson)}
            lang={globalLanguage}
            schemaAsObject={schemaAsObject}
            required={isFieldRequired("legal_representative")}
            // error={formErrors.personErrors}
            requirementException={false}
          // validationError={validationError?.legal_representative}
          />
        </Paper>

        {<Paper sx={{ p: 2 }}>
          <PersonSingle
            title={getTitleByField(schemaAsObject, "technical_contact")}
            subtitle={getDescriptionByField(schemaAsObject, "technical_contact")}
            schemaAsObject={schemaAsObject}
            id={"technical_contact"}
            initialPerson={formData.technical_contact}
            onChange={(updatedPerson) => handlePersonChange("technical_contact", updatedPerson)}
            lang={globalLanguage}
            required={isFieldRequired("technical_contact")}
            // error={false}
            requirementException={true}
          // validationError={validationError?.technical_contact}
          />
        </Paper>}

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {messages.eucontroledSectionTitle}
              </Typography>
              {/*<Typography variant="body2" color="text.secondary">
                {messages.eucontroledSectionSubTitle}
              </Typography>*/}
            </Box>

            <FormControl component="fieldset" fullWidth>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                  {getTitleByField(schemaAsObject, "eu_controlled_entity")}
                </FormLabel>
                <IconButton
                  size="small"
                  aria-label="More info about Yes"
                  onClick={handleOpen}
                >
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Box>
              <YesNoFieldWithAlert
                helperText={getDescriptionByField(schemaAsObject, "eu_controlled_entity")}
                value={formData.eu_controlled_entity}
                onChange={(value) => handleChange("eu_controlled_entity", value)}
                required={isFieldRequired("eu_controlled_entity")}
                yesLabel={messages.yesLabelParticipantForm}
                noLabel={messages.noLabelParticipantForm}
              />
            </FormControl>

            <Modal
              open={open}
              onClose={handleClose}
              aria-labelledby="more-info-text"
              aria-describedby="more-info-description"
            >
              <Box sx={{ ...style, width: 600 }}>
                <Alert severity="info" variant="outlined">
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{ __html: messages.eucontroledFirstNote }}
                  />
                </Alert>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 2,
                  }}
                >
                  <Button variant="contained" onClick={handleClose}>
                    {messages.close}
                  </Button>
                </Box>
              </Box>
            </Modal>

            {true && (
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.default",
                }}
              >
                <Stack spacing={2.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {messages.ocaSectionTitle}
                  </Typography>

                  <Typography variant="body1">
                    {messages.ocaProjectQuestionPartA}
                  </Typography>

                  <NumericTextField
                    label={getTitleByField(schemaAsObject, "pic_number")}
                    helperText={getDescriptionByField(schemaAsObject, "pic_number")}
                    value={formData.pic_number || ""}
                    onChange={(value) => handleChange("pic_number", value)}
                    required={isFieldRequired("pic_number")}
                  />

                  <Typography variant="body1">
                    {messages.ocaProjectQuestionPartB}
                  </Typography>

                  <SimpleTextField
                    label={getTitleByField(schemaAsObject, "oca_project")}
                    helperText={getDescriptionByField(schemaAsObject, "oca_project")}
                    value={formData.oca_project || ""}
                    onChange={(value) => handleChange("oca_project", value)}
                    required={isFieldRequired("oca_project")}
                  />
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <AddressView address={formData.other_address}
            handleChange={(updatedAddress) => handleAddressChange("other_address", updatedAddress)}
            fieldName="other_address"
            title={getTitleByField(schemaAsObject, "other_address")}
            lang={globalLanguage}
            countryOptions={country}
            schemaAsObject={schemaAsObject}
            subtitle={getDescriptionByField(schemaAsObject, "other_address")}
            required={isFieldRequired("other_address")}
          // error={false}
          />
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <LanguageValueField
              id={"short_name-id"}
              label={getTitleByField(schemaAsObject, "short_name")}
              helperText={getDescriptionByField(schemaAsObject, "short_name")}
              obj={formData?.short_name}
              lang={globalLanguage}
              required={isFieldRequired("short_name")}
              onChange={(updatedValues) => handleFieldChange("short_name", updatedValues)}
            // error={formErrors.allErrors.short_name}
            />

            <AutocompleteChipsView
              id={"activities-id"}
              label={getTitleByField(schemaAsObject, "activities")}
              helperText={getDescriptionByField(schemaAsObject, "activities")}
              // placeholder={getPlaceholderFromProperty(schemaAsObject, "activities")}
              required={isFieldRequired("activities")}
              objList={formData?.activities}
              lang={globalLanguage}
              onChange={(updatedValues) => handleFieldChange("activities", updatedValues)}
            />

            <LanguageValueField
              id={"description-id"}
              label={getTitleByField(schemaAsObject, "description")}
              helperText={getDescriptionByField(schemaAsObject, "description")}
              multiline={true}
              minRows={4}
              obj={formData?.description}
              lang={globalLanguage}
              required={isFieldRequired("description")}
              onChange={(updatedValues) => handleFieldChange("description", updatedValues)}
            // error={formErrors.allErrors.description}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <SimpleTextField
              label={getTitleByField(schemaAsObject, "eu_id") ? getTitleByField(schemaAsObject, "eu_id") : "eu_id"}
              helperText={getDescriptionByField(schemaAsObject, "eu_id") ? getDescriptionByField(schemaAsObject, "eu_id") : ""}
              value={formData.eu_id || ""}
              onChange={(value) => handleChange("eu_id", value)}
              required={isFieldRequired("eu_id")}
            // error={formErrors.allErrors.eu_id}
            />

            <AdmsIdentifierListView
              schemaAsObject={schemaAsObject}
              title={getTitleByField(schemaAsObject, "identifier")}
              subtitle={getDescriptionByField(schemaAsObject, "identifier")}
              obj={formData?.identifier.getAllModels()}
              lang={globalLanguage}
              onIdentifierChange={handleIdentifierChange}
              required={isFieldRequired("identifier")}
            />
          </Stack>
        </Paper>

        {validationError && <>
          <Stack sx={{ mt: 2 }}>{printError(validationError)}</Stack>
        </>}

        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <div style={{ padding: "1em" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasAcknowledgedTerms}
                    onChange={handleAcknowledgeChange}
                    disabled={isEditMode}
                    name="acknowledgeTerms"
                  />
                }
                label={
                  <Typography variant="h5">
                    {messages.participantFormConcentTextBefore}{" "}
                    <a
                      href="https://language-data-space.ec.europa.eu/general-terms-and-conditions-testing-language-data-space"
                      target="_blank" rel="noreferrer noopener">
                      {messages.participantFormTitleConcentTextAfter}
                    </a>.
                  </Typography>
                }
              />
            </div>
            <Alert severity="warning">
              <Typography variant="h5">{messages.participantFormPersonalDataBefore}
                <a href="https://language-data-space.ec.europa.eu/data-processing-agreement" target="_blank" rel="noreferrer noopener">
                  {messages.participantFormPersonalDataStatement}</a></Typography>
            </Alert>
          </Stack>
        </Paper>

        <Button onClick={handleSubmit} variant="contained" sx={{ p: 2 }}
          disabled={!hasAcknowledgedTerms || loading || isSubmitting}
          color="success"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
          {isEditMode ? "Update" : "Submit"}
        </Button>
      </Stack>)}
    {/* activeStep === 1 && (participantCreated || participantId) ? (
      <Paper sx={{ p: 2 }}>
        <DocumentUploadSection participantId={participantId} isEditMode={true} />
      </Paper>)
      : (<Paper sx={{ p: 2 }}>
        <Typography variant="h4" sx={{textAlign: "center"}} gutterBottom>{messages.completeStep1Message}</Typography>
      </Paper>)
      */ }
  </Container>
  );
};

export default ParticipantForm;
